import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { RoleRequests } from "@/components/RoleRequests";
import { DemoAccessPanel } from "@/components/admin/DemoAccess";
import { Footer } from "@/components/Footer";
import { Sprout, Building2, Plus, Loader2, FolderOpen, Trash2, Cpu, Landmark } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(null);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  useEffect(() => { load(); api.get("/quantum/status").then((r) => setQ(r.data)).catch(() => {}); }, []);

  const del = async (id) => {
    try { await api.delete(`/projects/${id}`); toast.success("Project deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#050A15]">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <RoleRequests />
        <DemoAccessPanel />
        {["admin", "collective_executive"].includes(user?.role) && (
          <Link to="/funding" data-testid="dashboard-funding-link" className="qs-panel mt-4 p-4 flex flex-wrap items-center justify-between gap-3 border-[#F59E0B]/40 hover:border-[#F59E0B] transition-colors">
            <div className="flex items-center gap-3"><Landmark className="h-5 w-5 text-[#F59E0B]" /><div><p className="text-slate-100 font-semibold text-sm">Funding Room — €1 M seed raise</p><p className="text-slate-400 text-xs">2-page summary (EN/TR) · fund-fit shortlist · pilot LOI pipeline · 30/60/90 plan</p></div></div>
            <span className="text-[#F59E0B] text-xs font-mono uppercase">Open →</span>
          </Link>
        )}
        <div className="flex items-end justify-between flex-wrap gap-4 mt-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Workspace</p>
            <h1 className="font-display font-bold text-3xl mt-1">Hello, {user?.name?.split(" ")[0]}</h1>
            <p className="text-slate-400 mt-1 text-sm">Role: <span className="font-mono text-[#00FFFF]">{user?.role}</span>{user?.role_request?.status === "pending" && <span className="ml-2 text-[#F59E0B] text-xs" data-testid="role-pending">· {user.role_request.role} request pending admin approval</span>}</p>
          </div>
          <button data-testid="new-project-btn" onClick={() => setOpen(true)}
            className="qs-cta inline-flex items-center gap-2 bg-[#00FFFF] text-[#050A15] font-semibold px-5 py-2.5">
            <Plus className="h-4 w-4" /> New Project
          </button>
        </div>

        {q && (
          <div className="qs-panel p-4 mt-6 flex flex-wrap items-center gap-3 text-sm">
            <Cpu className="h-5 w-5 text-[#8A2BE2]" />
            <span className="text-slate-300">Quantum backend:</span>
            <span className="font-mono text-[#00FF7F]" data-testid="quantum-status">{q.default_backend}</span>
            <span className="text-slate-500 hidden sm:inline" data-testid="quantum-hw-note">· {q.ibm_configured
              ? (q.ibm_hardware?.backend ? `QPU ${q.ibm_hardware.backend} · ${q.ibm_hardware.qubits} qubits · queue ${q.ibm_hardware.queue_length} · REST sampler` : `IBM Quantum configured${q.ibm_hardware?.error ? ` · ${q.ibm_hardware.error}` : ""}`)
              : (q.ibm_hardware?.present?.length ? `IBM Quantum partially configured · ${q.ibm_hardware.present.join(", ")} set · add ${q.ibm_hardware.missing.join(", ")} to run on real QPUs` : "auto-fallback to classical · add IBM_QUANTUM_TOKEN + IBM_QUANTUM_CRN to run on real QPUs via REST")}</span>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3 text-sm"><Link data-testid="dashboard-jev-report" to="/jev/report" className="border border-cyan-300/30 px-4 py-2 text-cyan-300 hover:bg-cyan-300/5 transition-colors">Monthly Jev tuning report →</Link><Link data-testid="dashboard-public-map" to="/alerts/map" className="border border-white/15 px-4 py-2 text-slate-300 hover:border-white/30 transition-colors">Public alert map →</Link></div>
        <div className="mt-8">
          {projects === null ? (
            <div className="grid place-items-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#00FFFF]" /></div>
          ) : projects.length === 0 ? (
            <div className="qs-panel p-12 text-center">
              <FolderOpen className="h-10 w-10 text-slate-600 mx-auto" />
              <h3 className="font-display font-semibold text-lg mt-4">No projects yet</h3>
              <p className="text-slate-400 text-sm mt-1">Create a project to run your first hyperspectral analysis.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-5" data-testid="projects-grid">
              {projects.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <div className="qs-panel qs-lift p-6 h-full flex flex-col">
                    <div className="flex items-start justify-between">
                      <span className="grid place-items-center h-10 w-10 rounded-sm"
                        style={{ background: p.domain === "agriculture" ? "#00FF7F1A" : "#F59E0B1A" }}>
                        {p.domain === "agriculture"
                          ? <Sprout className="h-5 w-5 text-[#00FF7F]" />
                          : <Building2 className="h-5 w-5 text-[#F59E0B]" />}
                      </span>
                      <button data-testid={`delete-project-${p.id}`} onClick={() => del(p.id)}
                        className="text-slate-600 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <h3 className="font-display font-semibold text-lg mt-4">{p.name}</h3>
                    <p className="text-xs text-slate-500 mt-1 capitalize">{p.domain} · {p.region || "—"}</p>
                    <p className="text-xs font-mono text-slate-400 mt-3">{p.scan_count} scan(s)</p>
                    <Link to={`/projects/${p.id}`} data-testid={`open-project-${p.id}`}
                      className="mt-4 text-sm text-[#00FFFF] hover:underline">Open workspace →</Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      {open && <NewProjectModal onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
      <Footer />
    </div>
  );
}

const NewProjectModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ name: "", domain: "agriculture", region: "", description: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/projects", form); toast.success("Project created"); onCreated(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="qs-panel w-full max-w-lg p-8" data-testid="new-project-modal">
        <h3 className="font-display font-bold text-xl">New Project</h3>
        <p className="text-slate-400 text-sm mt-1 mb-6">Pick a domain to configure the analysis engine.</p>

        <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Name</label>
        <input data-testid="project-name-input" required value={form.name} onChange={upd("name")}
          className="w-full bg-[#050A15] border border-white/10 px-4 py-3 mb-4 outline-none focus:border-[#00FFFF]" />

        <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Domain</label>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[["agriculture", "Agriculture", Sprout, "#00FF7F"], ["earthquake", "Earthquake", Building2, "#F59E0B"]].map(([v, l, Ic, c]) => (
            <button type="button" key={v} data-testid={`domain-${v}`} onClick={() => setForm({ ...form, domain: v })}
              className={`flex items-center gap-2 px-4 py-3 border transition-colors ${form.domain === v ? "border-[#00FFFF] bg-[#00FFFF]/5" : "border-white/10"}`}>
              <Ic className="h-5 w-5" style={{ color: c }} /> {l}
            </button>
          ))}
        </div>

        <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Region</label>
        <input data-testid="project-region-input" value={form.region} onChange={upd("region")}
          placeholder="e.g. İzmir, Konak" className="w-full bg-[#050A15] border border-white/10 px-4 py-3 mb-6 outline-none focus:border-[#00FFFF]" />

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 border border-white/10 py-3 hover:border-white/30 transition-colors">Cancel</button>
          <button data-testid="create-project-submit" disabled={loading}
            className="qs-cta flex-1 bg-[#00FFFF] text-[#050A15] font-semibold py-3 flex items-center justify-center disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
};
